import { useState, useRef, useCallback, useEffect } from "react";
import { X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ZoomableImageProps {
  src: string;
  alt?: string;
  className?: string;
  thumbnailClass?: string;
}

export function ZoomableImage({ src, alt = "Image", className, thumbnailClass }: ZoomableImageProps) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const lastTouchDist = useRef<number | null>(null);

  const clamp = (v: number) => Math.min(10, Math.max(1, v));

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const openLightbox = () => { setOpen(true); reset(); };
  const closeLightbox = () => { setOpen(false); reset(); };

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") closeLightbox(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale(prev => clamp(prev * (1 - e.deltaY * 0.001)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStart.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
    setDragging(true);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current) return;
    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  };
  const handleMouseUp = () => {
    dragStart.current = null;
    setDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1) {
      dragStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        ox: offset.x,
        oy: offset.y,
      };
    }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 2 && lastTouchDist.current !== null) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      setScale(prev => clamp(prev * (dist / lastTouchDist.current!)));
      lastTouchDist.current = dist;
    } else if (e.touches.length === 1 && dragStart.current) {
      setOffset({
        x: dragStart.current.ox + (e.touches[0].clientX - dragStart.current.x),
        y: dragStart.current.oy + (e.touches[0].clientY - dragStart.current.y),
      });
    }
  };
  const handleTouchEnd = () => {
    lastTouchDist.current = null;
    dragStart.current = null;
  };

  return (
    <>
      <div
        className={cn("relative group cursor-zoom-in", className)}
        onClick={openLightbox}
        title="Click to zoom"
      >
        <img
          src={src}
          alt={alt}
          className={cn(
            "w-full object-contain rounded-md border border-border bg-secondary/20",
            thumbnailClass ?? "max-h-64"
          )}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity rounded-md pointer-events-none">
          <Maximize2 className="w-8 h-8 text-white drop-shadow" />
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black/95 touch-none">
          <div className="flex items-center justify-between p-3 shrink-0 bg-black/70 backdrop-blur-sm border-b border-white/10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setScale(s => clamp(s * 1.4))}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-white transition-colors"
                title="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                onClick={() => setScale(s => clamp(s / 1.4))}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-white transition-colors"
                title="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-white text-xs font-mono transition-colors min-w-[52px] text-center"
                title="Reset zoom"
              >
                {Math.round(scale * 100)}%
              </button>
            </div>
            <button
              onClick={closeLightbox}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 text-white transition-colors"
              title="Close (Esc)"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div
            className="flex-1 overflow-hidden relative select-none"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ cursor: scale > 1 ? (dragging ? "grabbing" : "grab") : "zoom-out" }}
            onClick={scale === 1 ? closeLightbox : undefined}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`,
                transformOrigin: "center",
                userSelect: "none",
                transition: dragging ? "none" : "transform 0.15s ease",
              }}
            />
          </div>

          <p className="text-center text-white/40 text-xs font-mono py-2 shrink-0">
            {scale === 1 ? "SCROLL OR PINCH TO ZOOM · TAP TO CLOSE" : "DRAG TO PAN · PINCH OR SCROLL TO ZOOM"}
          </p>
        </div>
      )}
    </>
  );
}
